import React, { useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  BRAND, ConceitosLegenda, ConceitosRows, KpiCard, RankedBars, SectionCard,
  fmt, mediaColor, monthLabel, pct, rateColor,
} from './PainelDiarioUI';
import { CONCEITOS, COR_DO_CONCEITO } from '../utils/habilidades';

/**
 * Desempenho do aluno no ano, a partir do que os professores lançam no
 * diário: notas por bimestre, conceitos de habilidade (1º/2º ano e Educação
 * Infantil) e frequência por aula. Dados de /students/<id>/desempenho/.
 *
 * O Perfil do Aluno nasceu antes do diário e só mostrava provas e
 * descritores — uma aluna com o boletim inteiro aparecia com "Média Geral
 * 0,0%". As regras (aprovação 7,0, crítico abaixo de 5,0, frequência mínima
 * 75%) são as do Painel da Rede, com os mesmos blocos visuais.
 */

const SITUACAO = {
  aprovado: { label: 'Na média', cls: 'bg-[#13983D]/10 text-[#13983D]' },
  recuperacao: { label: 'Abaixo de 7,0', cls: 'bg-[#F9B047]/15 text-[#B87608]' },
  critico: { label: 'Crítica', cls: 'bg-[#E83A3C]/10 text-[#E83A3C]' },
};

function SituacaoNota({ situacao }) {
  const s = SITUACAO[situacao];
  if (!s) return <span className="text-xs text-slate-400">—</span>;
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

function toneDaMedia(media) {
  if (media === null || media === undefined) return 'accent';
  if (media >= 7) return 'good';
  if (media >= 5) return 'warn';
  return 'crit';
}

function toneDaFrequencia(taxa) {
  if (taxa === null || taxa === undefined) return 'accent';
  if (taxa >= 90) return 'good';
  if (taxa >= 75) return 'warn';
  return 'crit';
}

/** Os quatro números do topo do perfil. */
export function DesempenhoKpis({ desempenho, provas, darkMode, onIrPara }) {
  const notas = desempenho?.notas;
  const freq = desempenho?.frequencia;
  const hab = desempenho?.habilidades;
  const temNotas = Boolean(notas?.disciplinas?.length);
  const abaixo = notas?.abaixo_da_media || [];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {temNotas || !hab ? (
        <KpiCard
          darkMode={darkMode}
          label="Média geral no diário"
          value={notas?.media_geral !== null && notas?.media_geral !== undefined ? fmt(notas.media_geral) : '—'}
          tone={toneDaMedia(notas?.media_geral)}
          foot={temNotas ? `${notas.disciplinas.length} disciplina(s) com nota` : 'Nenhuma nota lançada'}
          onClick={() => onIrPara('desempenho')}
        />
      ) : (
        <KpiCard
          darkMode={darkMode}
          label="Habilidades consolidadas"
          value={fmt(hab.resumo.HC?.pct)}
          unit="%"
          tone={(hab.resumo.HC?.pct || 0) >= 50 ? 'good' : 'warn'}
          foot={`${hab.resumo.HC?.count || 0} de ${hab.resumo.total} habilidades`}
          onClick={() => onIrPara('desempenho')}
        />
      )}
      <KpiCard
        darkMode={darkMode}
        label="Frequência"
        value={freq?.taxa !== null && freq?.taxa !== undefined ? fmt(freq.taxa) : '—'}
        unit={freq?.taxa !== null && freq?.taxa !== undefined ? '%' : undefined}
        tone={toneDaFrequencia(freq?.taxa)}
        foot={freq?.aulas
          ? `${freq.faltas} falta(s) e ${freq.justificadas} justificada(s) em ${freq.aulas} aulas`
          : 'Nenhuma chamada lançada'}
        onClick={() => onIrPara('desempenho')}
      />
      {!temNotas && hab ? (
        // 1º/2º ano e Infantil não lançam nota: o número que importa é o de
        // habilidades que ainda não apareceram.
        <KpiCard
          darkMode={darkMode}
          label="Habilidades não desenvolvidas"
          value={hab.resumo.HND?.count || 0}
          tone={(hab.resumo.HND?.count || 0) ? 'warn' : 'good'}
          foot={`HND no conceito mais recente · ${fmt(hab.resumo.HND?.pct)}%`}
          onClick={() => onIrPara('desempenho')}
        />
      ) : (
        <KpiCard
          darkMode={darkMode}
          label="Disciplinas abaixo de 7,0"
          value={temNotas ? abaixo.length : '—'}
          tone={!temNotas ? 'accent' : abaixo.length ? 'warn' : 'good'}
          foot={abaixo.length ? abaixo.slice(0, 3).join(', ') + (abaixo.length > 3 ? '…' : '') : temNotas ? 'Todas na média' : 'Nenhuma nota lançada'}
          onClick={() => onIrPara('desempenho')}
        />
      )}
      <KpiCard
        darkMode={darkMode}
        label="Avaliações externas"
        value={provas.realizadas}
        tone="nav"
        foot={provas.realizadas ? `Acerto médio de ${provas.media}%` : 'Nenhuma prova realizada'}
        onClick={() => onIrPara('provas')}
      />
    </div>
  );
}

function Alertas({ alertas, darkMode }) {
  if (!alertas?.length) return null;
  return (
    <div className={`rounded-2xl p-4 ${darkMode ? 'bg-[#F9B047]/10 border border-[#F9B047]/25' : 'bg-[#F9B047]/10 border border-[#F9B047]/35'}`}>
      <h3 className={`font-semibold text-sm mb-2 ${darkMode ? 'text-[#F9B047]' : 'text-[#9A6B16]'}`}>
        Pontos de atenção
      </h3>
      <ul className="space-y-1">
        {alertas.map((a) => (
          <li key={a.texto} className={`text-sm flex gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            <span aria-hidden>{a.nivel === 'critico' ? '🔴' : '🟡'}</span>
            <span><span className="sr-only">{a.nivel === 'critico' ? 'Crítico: ' : 'Atenção: '}</span>{a.texto}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Boletim({ notas, darkMode }) {
  const th = `px-3 py-2 text-xs font-medium text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const td = 'px-3 py-2.5 text-center tabular-nums';
  const nota = (v) => (
    <span className={`font-semibold ${mediaColor(v)}`}>{v === null || v === undefined ? '—' : fmt(v)}</span>
  );
  return (
    <>
    {/* No celular a tabela rolava para o lado e escondia justamente a média
        e a situação. Lá, um cartão por disciplina. */}
    <div className="md:hidden space-y-2">
      {notas.disciplinas.map((d) => (
        <div key={d.id_subject} className={`rounded-xl p-3 ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{d.subject_name}</span>
            <span className="flex items-center gap-2">
              <span className={`text-lg font-bold tabular-nums ${mediaColor(d.media)}`}>{d.media === null ? '—' : fmt(d.media)}</span>
              <SituacaoNota situacao={d.situacao} />
            </span>
          </div>
          <div className={`mt-1 text-xs tabular-nums flex flex-wrap gap-x-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {d.bimestres.map((b, i) => (
              <span key={i}>{i + 1}º <b className={mediaColor(b)}>{b === null ? '—' : fmt(b)}</b></span>
            ))}
            {d.prova_final !== null && <span>Final <b className={mediaColor(d.prova_final)}>{fmt(d.prova_final)}</b></span>}
            <span>Turma {d.media_turma === null ? '—' : fmt(d.media_turma)}</span>
          </div>
        </div>
      ))}
    </div>
    <div className="hidden md:block overflow-x-auto -mx-1">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className={darkMode ? 'border-b border-slate-700' : 'border-b border-slate-200'}>
            <th className={`${th} text-left`}>Disciplina</th>
            <th className={th}>1º bim</th>
            <th className={th}>2º bim</th>
            <th className={th}>3º bim</th>
            <th className={th}>4º bim</th>
            <th className={th}>Prova final</th>
            <th className={th}>Média</th>
            <th className={th} title="Média anual da turma na disciplina">Turma</th>
            <th className={th}>Situação</th>
          </tr>
        </thead>
        <tbody>
          {notas.disciplinas.map((d) => (
            <tr key={d.id_subject} className={darkMode ? 'border-b border-slate-700/60' : 'border-b border-slate-100'}>
              <td className={`px-3 py-2.5 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{d.subject_name}</td>
              {d.bimestres.map((b, i) => <td key={i} className={td}>{nota(b)}</td>)}
              <td className={td}>{nota(d.prova_final)}</td>
              <td className={td}><span className={`text-base font-bold ${mediaColor(d.media)}`}>{d.media === null ? '—' : fmt(d.media)}</span></td>
              <td className={`${td} ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{d.media_turma === null ? '—' : fmt(d.media_turma)}</td>
              <td className={td}><SituacaoNota situacao={d.situacao} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className={`text-[11px] mt-2 px-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
      Média do bimestre: cada nota vale a maior entre a avaliação e a recuperação. Média anual: média dos bimestres com nota.
    </p>
    </>
  );
}

function EvolucaoBimestral({ evolucao, darkMode }) {
  const dados = evolucao.map((e) => ({ nome: `${e.bimestre}º bim`, Aluno: e.media, Turma: e.media_turma }));
  if (!dados.some((d) => d.Aluno !== null)) {
    return <div className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma nota lançada ainda.</div>;
  }
  const eixo = darkMode ? '#94a3b8' : '#64748b';
  const grade = darkMode ? '#334155' : '#e2e8f0';
  const turma = darkMode ? '#94a3b8' : '#94a3b8';
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={dados} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
          <CartesianGrid stroke={grade} vertical={false} />
          <XAxis dataKey="nome" tick={{ fontSize: 11, fill: eixo }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 10]} ticks={[0, 5, 7, 10]} tick={{ fontSize: 11, fill: eixo }} axisLine={false} tickLine={false} />
          <ReferenceLine y={7} stroke={BRAND.good} strokeDasharray="4 4" label={{ value: 'Aprovação 7,0', position: 'insideTopRight', fontSize: 10, fill: eixo }} />
          <Tooltip
            formatter={(v, nome) => [v === null || v === undefined ? '—' : fmt(v), nome === 'Aluno' ? 'Média do aluno' : 'Média da turma']}
            contentStyle={{ borderRadius: 8, fontSize: 12, background: darkMode ? '#1e293b' : '#fff', border: `1px solid ${grade}` }}
            labelStyle={{ color: darkMode ? '#e2e8f0' : '#334155' }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => (v === 'Aluno' ? 'Aluno' : 'Média da turma')} />
          <Line type="monotone" dataKey="Turma" stroke={turma} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 4 }} connectNulls />
          <Line type="monotone" dataKey="Aluno" stroke={BRAND.accent} strokeWidth={2.5} dot={{ r: 5, strokeWidth: 2, fill: darkMode ? '#0f172a' : '#fff' }} activeDot={{ r: 6 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function FrequenciaMensal({ meses, darkMode }) {
  if (!meses?.length) {
    return <div className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma chamada lançada.</div>;
  }
  const cor = (t) => (t >= 90 ? BRAND.good : t >= 75 ? BRAND.warn : BRAND.crit);
  return (
    <div className="relative">
      <div className="flex items-end gap-1.5 sm:gap-2 h-36 pt-2">
        {meses.map((m) => (
          <div
            key={m.month}
            className="flex-1 flex flex-col items-center justify-end gap-1 h-full"
            title={`${monthLabel(m.month)}: ${pct(m.taxa)} — ${m.faltas} falta(s), ${m.justificadas} justificada(s) em ${m.aulas} aulas`}
          >
            <span className={`text-[10px] tabular-nums font-semibold ${rateColor(m.taxa)}`}>{fmt(m.taxa)}%</span>
            <div className="w-full max-w-[26px] rounded-t" style={{ height: `${m.taxa || 0}%`, minHeight: 4, background: cor(m.taxa) }} />
            <span className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{monthLabel(m.month)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChipConceito({ conceito, darkMode }) {
  if (!conceito) {
    return <span className={`inline-block w-10 text-center text-[10px] rounded py-0.5 ${darkMode ? 'bg-slate-700/60 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>—</span>;
  }
  return (
    <span
      className="inline-block w-10 text-center text-[10px] font-bold rounded py-0.5 text-white"
      style={{ background: COR_DO_CONCEITO[conceito] }}
    >
      {conceito}
    </span>
  );
}

function HabilidadesDetalhe({ habilidades, darkMode }) {
  const [aberto, setAberto] = useState(null);
  return (
    <div className="space-y-2">
      {habilidades.componentes.map((c) => (
        <div key={c.componente} className={`rounded-xl ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
          <button
            type="button"
            onClick={() => setAberto(aberto === c.componente ? null : c.componente)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{c.componente}</span>
            <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {c.contagem.HC?.count || 0} de {c.contagem.total} consolidadas {aberto === c.componente ? '▲' : '▼'}
            </span>
          </button>
          {aberto === c.componente && (
            <div className="px-4 pb-3 overflow-x-auto">
              <table className="w-full text-xs min-w-[520px]">
                <thead>
                  <tr className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                    <th className="text-left font-medium py-1">Habilidade</th>
                    {[1, 2, 3, 4].map((b) => <th key={b} className="font-medium py-1 w-12">{b}º bim</th>)}
                  </tr>
                </thead>
                <tbody>
                  {c.habilidades.map((h) => (
                    <tr key={h.codigo} className={darkMode ? 'border-t border-slate-700/60' : 'border-t border-slate-200'}>
                      <td className={`py-2 pr-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <b className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{h.codigo}</b> {h.descricao}
                      </td>
                      {h.conceitos.map((conc, i) => (
                        <td key={i} className="py-2 text-center"><ChipConceito conceito={conc} darkMode={darkMode} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function UltimasFaltas({ faltas, darkMode }) {
  if (!faltas?.length) {
    return <div className={`text-sm py-4 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma falta registrada.</div>;
  }
  return (
    <ul className={`sm:columns-2 sm:gap-8 divide-y ${darkMode ? 'divide-slate-700/60' : 'divide-slate-100'}`}>
      {faltas.map((f, i) => (
        <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm break-inside-avoid">
          <div className="min-w-0">
            <span className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {new Date(f.data + 'T00:00:00').toLocaleDateString('pt-BR')}
            </span>
            <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              {' · '}{f.disciplina}{f.aula ? ` · ${f.aula}ª aula` : ''}
            </span>
            {f.justificativa && (
              <div className={`text-xs truncate ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} title={f.justificativa}>{f.justificativa}</div>
            )}
          </div>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${f.status === 'justificado'
            ? 'bg-[#F9B047]/15 text-[#B87608]'
            : 'bg-[#E83A3C]/10 text-[#E83A3C]'}`}>
            {f.status === 'justificado' ? 'Justificada' : 'Falta'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** A aba "Desempenho" do perfil. */
export default function DesempenhoAluno({ desempenho, darkMode }) {
  if (!desempenho) {
    return <div className={`text-sm py-10 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Não foi possível carregar o desempenho.</div>;
  }
  if (!desempenho.turma) {
    return <div className={`text-sm py-10 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>O aluno não está em nenhuma turma.</div>;
  }
  const { notas, habilidades, frequencia, alertas, ano } = desempenho;
  const temNotas = Boolean(notas?.disciplinas?.length);
  const nada = !temNotas && !habilidades && !frequencia?.aulas;

  if (nada) {
    return (
      <SectionCard darkMode={darkMode} title={`Desempenho em ${ano}`}>
        <p className={`text-sm py-6 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          Ainda não há notas, habilidades ou chamadas lançadas no diário para este aluno em {ano}.
        </p>
      </SectionCard>
    );
  }

  return (
    <div className="space-y-6">
      <Alertas alertas={alertas} darkMode={darkMode} />

      {temNotas && (
        <SectionCard darkMode={darkMode} title={`Boletim ${ano}`} subtitle="Notas lançadas no diário, por disciplina e bimestre">
          <Boletim notas={notas} darkMode={darkMode} />
        </SectionCard>
      )}

      {temNotas && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard darkMode={darkMode} title="Evolução por bimestre" subtitle="Média do aluno entre as disciplinas, ao lado da média da turma">
            <EvolucaoBimestral evolucao={notas.evolucao} darkMode={darkMode} />
          </SectionCard>
          <SectionCard darkMode={darkMode} title="Média por disciplina" subtitle="Média anual; a linha marca a aprovação (7,0)">
            <RankedBars
              darkMode={darkMode}
              mode="nota"
              suffix=""
              max={10}
              rows={notas.disciplinas.map((d) => ({ label: d.subject_name, value: d.media, sub: d.media_turma !== null ? `turma ${fmt(d.media_turma)}` : undefined }))}
            />
          </SectionCard>
        </div>
      )}

      {habilidades && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard darkMode={darkMode} title="Habilidades por componente" subtitle="Conceito mais recente de cada habilidade">
            <ConceitosRows
              darkMode={darkMode}
              rows={habilidades.componentes.map((c) => ({ label: c.componente, contagem: c.contagem }))}
            />
            <ConceitosLegenda darkMode={darkMode} />
          </SectionCard>
          <SectionCard darkMode={darkMode} title="Evolução das habilidades" subtitle="Conceitos lançados em cada bimestre">
            <ConceitosRows
              darkMode={darkMode}
              rows={habilidades.por_bimestre.map((b) => ({ label: `${b.bimestre}º bimestre`, contagem: b.contagem }))}
            />
          </SectionCard>
          <SectionCard darkMode={darkMode} title="Resumo" subtitle={`Até o ${habilidades.ultimo_bimestre}º bimestre`}>
            <div className="space-y-2">
              {CONCEITOS.slice().reverse().map((c) => (
                <div key={c} className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-2">
                    <ChipConceito conceito={c} darkMode={darkMode} />
                  </span>
                  <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                    <b className={darkMode ? 'text-slate-100' : 'text-slate-800'}>{habilidades.resumo[c]?.count || 0}</b>
                    {' '}({fmt(habilidades.resumo[c]?.pct)}%)
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
          <SectionCard darkMode={darkMode} className="lg:col-span-3" title="Habilidades, uma a uma" subtitle="Clique no componente para ver o conceito de cada habilidade em cada bimestre">
            <HabilidadesDetalhe habilidades={habilidades} darkMode={darkMode} />
          </SectionCard>
        </div>
      )}

      {frequencia?.aulas > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard darkMode={darkMode} title="Frequência por disciplina" subtitle="Presença nas aulas com chamada; a linha marca o mínimo de 75%">
            <RankedBars
              darkMode={darkMode}
              mode="freq"
              rows={frequencia.por_disciplina.map((d) => ({ label: d.subject_name, value: d.taxa, sub: `${d.faltas + d.justificadas} falta(s)` }))}
            />
          </SectionCard>
          <SectionCard darkMode={darkMode} title="Frequência por mês" subtitle="A falta justificada conta como falta nos 75%">
            <FrequenciaMensal meses={frequencia.por_mes} darkMode={darkMode} />
          </SectionCard>
          <SectionCard darkMode={darkMode} className="lg:col-span-2" title="Últimas faltas" subtitle={`${frequencia.faltas + frequencia.justificadas} no ano`}>
            <UltimasFaltas faltas={frequencia.ultimas_faltas} darkMode={darkMode} />
          </SectionCard>
        </div>
      )}
    </div>
  );
}
